import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { runWhenAnalyticsConsented } from './cookieConsent';
import { configureGa4, ensureGa4Script, fireGa4PageView } from './ga4Client';

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
    snaptr: any;
    ttq: any;
    gtag: any;
    dataLayer: any[];
  }
}

interface PixelLoaderProps {
  metaPixelId?: string;
  snapchatPixelId?: string;
  tiktokPixelId?: string;
  ga4MeasurementId?: string;
  yandexCounterId?: string;
}

// ⏱️ تأخير تحميل البيكسلات لتحسين سرعة الصفحة (3 ثواني)
const PIXEL_LOAD_DELAY = 3000;

export const PixelLoader: React.FC<PixelLoaderProps> = ({
  metaPixelId = import.meta.env.VITE_META_PIXEL_ID,
  snapchatPixelId = import.meta.env.VITE_SNAPCHAT_PIXEL_ID,
  tiktokPixelId = import.meta.env.VITE_TIKTOK_PIXEL_ID,
  ga4MeasurementId = import.meta.env.VITE_GA4_MEASUREMENT_ID,
  yandexCounterId = import.meta.env.VITE_YANDEX_COUNTER_ID,
}) => {
  const location = useLocation();
  

  // Initialize Meta Pixel (Facebook) - مع تأخير 3 ثواني
  useEffect(() => {
    if (!metaPixelId) return;

    const timeoutId = setTimeout(() => {
    if (!window.fbq) {
      const f = function () {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        f.callMethod ? f.callMethod.apply(f, arguments) : f.queue.push(arguments);
      } as any;
      
      if (!window.fbq) window.fbq = f;
      if (!window._fbq) window._fbq = f;
      
      f.push = f;
      f.loaded = true;
      f.version = '2.0';
      f.queue = [];
      
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      
      // ✅ إطلاق custom event للإشارة إلى أن Meta Pixel جاهز
      script.onload = () => {
        window.dispatchEvent(new CustomEvent('metaPixelReady', { detail: { pixelId: metaPixelId } }));
      };
      
      document.head.appendChild(script);

      window.fbq('init', metaPixelId);
    }
    
    // تأكد من أن fbq موجود قبل الاستدعاء
    // ⚠️ PageView يتم إرساله من event-tracker.ts مع eventID للـ deduplication
    // لذلك لا نرسله هنا لتجنب التكرار
    // if (window.fbq) {
    //   window.fbq('track', 'PageView');
    // }
    }, PIXEL_LOAD_DELAY); // ⏱️ تأخير 3 ثواني

    return () => clearTimeout(timeoutId);
  }, [metaPixelId, location.pathname]);

  // Initialize Snapchat Pixel - مع تأخير 3 ثواني
  useEffect(() => {
    if (!snapchatPixelId) {
      return;
    }

    const timeoutId = setTimeout(() => {

    if (!window.snaptr) {
      const s = function () {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        s.handleRequest ? s.handleRequest.apply(s, arguments) : s.queue.push(arguments);
      } as any;
      
      if (!window.snaptr) window.snaptr = s;

      s.queue = [];
      s._loaded = false; // ✅ علامة لتتبع حالة التحميل
      s._loadAttempts = 0; // عدد محاولات التحميل
      
      // ✅ URLs للتحميل (الأصلي + البدائل)
      // الأولوية: Cloudflare Proxy (لتجاوز قيود الشبكة محلياً) > الرابط الأصلي
      const SDK_URLS = [
        'https://teen-sa.com/snap-pixel/scevent.min.js', // ✅ Cloudflare Workers Proxy (يحل مشكلة الحجب)
        'https://sc-static.net/scevent.min.js', // الأصلي (fallback)
      ];
      
      // ✅ دالة لتحميل SDK مع fallback
      const loadSnapchatSDK = (urlIndex: number = 0) => {
        if (urlIndex >= SDK_URLS.length) {
          return;
        }
        
        const currentUrl = SDK_URLS[urlIndex];
        s._loadAttempts++;
        
        const script = document.createElement('script');
        script.async = true;
        script.src = currentUrl + (urlIndex > 0 ? '?t=' + Date.now() : ''); // cache bust للـ retry
        
        // ✅ Timeout للتحميل (10 ثواني)
        const loadTimeout = setTimeout(() => {
          script.onerror?.(new Event('timeout'));
        }, 10000);
        
        script.onerror = () => {
          clearTimeout(loadTimeout);
          
          // ✅ محاولة الـ URL التالي
          setTimeout(() => loadSnapchatSDK(urlIndex + 1), 1000);
        };
        
        script.onload = () => {
          clearTimeout(loadTimeout);
          s._loaded = true;
          
          // ✅ إطلاق custom event للإشارة إلى أن Snapchat Pixel جاهز
          window.dispatchEvent(new CustomEvent('snapchatPixelReady', { detail: { pixelId: snapchatPixelId } }));
        };
        
        document.head.appendChild(script);
      };
      
      // ✅ بدء التحميل
      loadSnapchatSDK(0);

      window.snaptr('init', snapchatPixelId);
    }

    // ✅ إصلاح: لا نرسل PAGE_VIEW تلقائياً هنا
    // ⚠️ PAGE_VIEW يتم إرساله من event-tracker.ts مع client_dedup_id للـ deduplication
    // لذلك لا نرسله هنا لتجنب التكرار وضمان وجود event_id
    // if (window.snaptr) {
    //   window.snaptr('track', 'PAGE_VIEW');
    // }
    }, PIXEL_LOAD_DELAY); // ⏱️ تأخير 3 ثواني

    return () => clearTimeout(timeoutId);
  }, [snapchatPixelId, location.pathname]);

  // Initialize TikTok Pixel - مع تأخير 3 ثواني
  useEffect(() => {
    if (!tiktokPixelId) {
      return;
    }

    const timeoutId = setTimeout(() => {

    if (!window.ttq) {
      const t = function () {
        // @ts-ignore
        // eslint-disable-next-line prefer-rest-params
        t.callMethod ? t.callMethod.apply(t, arguments) : t.queue.push(arguments);
      } as any;
      
      t.push = t;
      t.loaded = true;
      t.version = '1.1';
      t.queue = [];
      t._i = {}; // ✅ مهم لـ ttq.load

      // تعريف الدوال الفرعية لـ TikTok
      t.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
      t.setAndDefer = function(target: any, method: any) {
        target[method] = function() {
          target.push([method].concat(Array.prototype.slice.call(arguments, 0)))
        }
      };
      for (let i = 0; i < t.methods.length; i++) {
        t.setAndDefer(t, t.methods[i]);
      }
      
      // ✅ تعريف ttq.load بشكل خاص (حسب الكود الرسمي من TikTok)
      t.load = function(pixelId: string) {
        const scriptUrl = 'https://analytics.tiktok.com/i18n/pixel/events.js';
        t._i[pixelId] = [];
        t._t = t._t || {};
        t._t[pixelId] = +new Date();
        
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = scriptUrl + '?sdkid=' + pixelId + '&lib=ttq'; // ✅ مع query parameters!
        
        // ✅ إضافة error handling (قبل إضافة السكريبت إلى DOM)
        script.onerror = () => {
          // ✅ إزالة علامة loaded عند الفشل
          t.loaded = false;
        };
        
        script.onload = () => {
          // ✅ انتظار حتى يتم تهيئة TikTok Pixel SDK بالكامل
          // TikTok Pixel SDK يحتاج وقت إضافي بعد تحميل السكريبت
          const checkReady = (attempts: number = 0) => {
            const maxAttempts = 20; // 10 ثواني كحد أقصى
            if (attempts >= maxAttempts) {
              return;
            }
            
            // ✅ التحقق من أن ttq.track متاح (ttq قد يكون function أو object)
            const ttq = (window as any).ttq;
            const isReady = (typeof ttq === 'object' || typeof ttq === 'function') && 
                           ttq !== null && 
                           typeof ttq.track === 'function';
            
            if (isReady) {
              // ✅ إطلاق custom event للإشارة إلى أن TikTok Pixel جاهز
              if (typeof window !== 'undefined' && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent('tiktokPixelReady', { 
                  detail: { pixelId } 
                }));
              }
            } else {
              // ✅ إعادة المحاولة بعد 500ms
              setTimeout(() => checkReady(attempts + 1), 500);
            }
          };
          
          // ✅ بدء التحقق بعد 100ms (إعطاء وقت للسكريبت للتهيئة)
          setTimeout(() => checkReady(), 100);
        };
        
        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          document.head.appendChild(script);
        }
      };
      
      // ✅ تعريف TiktokAnalyticsObject قبل استدعاء ttq.load
      (window as any).TiktokAnalyticsObject = 'ttq';
      window.ttq = t;

      // ✅ الآن نستدعي ttq.load - هي ستقوم بتحميل السكريبت بنفسها
      window.ttq.load(tiktokPixelId);
      // ✅ إصلاح: لا نرسل page() تلقائياً هنا
      // ⚠️ PageView يتم إرساله من event-tracker.ts مع event_id للـ deduplication
      // لذلك لا نرسله هنا لتجنب التكرار وضمان وجود event_id
      // window.ttq.page();
    }
    // ✅ إصلاح: لا نرسل page() تلقائياً عند تغيير المسار
    // ⚠️ PageView يتم إرساله من event-tracker.ts مع event_id للـ deduplication
    // if (window.ttq && typeof window.ttq.page === 'function') {
    //   window.ttq.page();
    // }
    }, PIXEL_LOAD_DELAY); // ⏱️ تأخير 3 ثواني

    return () => clearTimeout(timeoutId);
  }, [tiktokPixelId, location.pathname]);

  // Initialize GA4 - مع تأخير 3 ثواني
  useEffect(() => {
    if (!ga4MeasurementId) return;

    const timeoutId = setTimeout(() => {
      const getPageTitle = () => {
        const currentTitle = document.title;
        if (currentTitle === 'اطلب | Otlob') {
          const pathToTitle: Record<string, string> = {
            '/': 'الصفحة الرئيسية - اطلب',
            '/checkout': 'إتمام الطلب - اطلب',
            '/success': 'تم الدفع بنجاح - اطلب',
            '/failure': 'فشل الدفع - اطلب',
          };
          if (location.pathname.startsWith('/success')) {
            return pathToTitle['/success'];
          }
          return pathToTitle[location.pathname] || currentTitle;
        }
        return currentTitle;
      };

      runWhenAnalyticsConsented(() => {
        void ensureGa4Script(ga4MeasurementId).then(() => {
          configureGa4(ga4MeasurementId);
          // الصفحة الرئيسية تُرسل عبر eventHub.pageView — باقي المسارات هنا
          if (location.pathname !== '/') {
            fireGa4PageView({
              page_title: getPageTitle(),
              page_location: window.location.href,
              page_path: location.pathname,
            });
          }
        });
      });
    }, PIXEL_LOAD_DELAY);

    return () => clearTimeout(timeoutId);
  }, [ga4MeasurementId, location.pathname]);

  // Initialize Yandex Metrika - مع تأخير 3 ثواني
  useEffect(() => {
    if (!yandexCounterId) return;

    const timeoutId = setTimeout(() => {
    try {
      // Yandex Metrika uses a specific initialization pattern
      // ⚠️ يجب إنشاء ym queue قبل تحميل script
      if (!(window as any).ym) {
        // إنشاء queue function (نمط Yandex الرسمي)
        (window as any).ym = function(...args: any[]) {
          ((window as any).ym.a = (window as any).ym.a || []).push(args);
        };
        (window as any).ym.a = (window as any).ym.a || [];
        (window as any).ym.l = 1 * new Date().getTime();

        // ✅ يجب وجود window.dataLayer قبل التهيئة: مع ecommerce:true يربط Yandex
        // حاوية الـ ecommerce بـ window.dataLayer عند init، فإعادة إنشائها لاحقاً تكسر المرجع.
        if (!(window as any).dataLayer) {
          (window as any).dataLayer = [];
        }

        (window as any).ym(Number(yandexCounterId), 'init', {
          clickmap: true,
          trackLinks: true,
          accurateTrackBounce: true,
          webvisor: true,
          trackHash: true,
          ecommerce: true,
        });

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = `https://mc.yandex.ru/metrika/tag.js`;
        script.onerror = () => {
          // تجاهل هادئ — Yandex قد يكون محجوباً بـ ad blocker
        };
        document.head.appendChild(script);

        try {
          const noscript = document.createElement('noscript');
          const noscriptImg = document.createElement('img');
          noscriptImg.src = `https://mc.yandex.ru/watch/${yandexCounterId}`;
          noscriptImg.style.position = 'absolute';
          noscriptImg.style.left = '-9999px';
          noscriptImg.alt = '';
          noscript.appendChild(noscriptImg);
          if (document.body) {
            document.body.appendChild(noscript);
          } else {
            window.addEventListener('DOMContentLoaded', () => {
              if (document.body) document.body.appendChild(noscript);
            }, { once: true });
          }
        } catch (_) {
          // noscript fallback فشل — غير حرج
        }
      } else {
        if (typeof (window as any).ym === 'function') {
          (window as any).ym(Number(yandexCounterId), 'hit', location.pathname);
        }
      }
    } catch (_) {
      // Yandex Metrika فشل في التهيئة — تجاهل هادئ (ad blocker أو network error)
    }
    }, PIXEL_LOAD_DELAY); // ⏱️ تأخير 3 ثواني

    return () => clearTimeout(timeoutId);
  }, [yandexCounterId, location.pathname]);

  return null;
};
